import { Fragment, useMemo } from 'react';
import { useTranslation } from '@yorukiri/i18n';
import { colors } from '@yorukiri/ui/tokens';
import { useMediaBlobUrl } from '../lib/mediaCache';
import type { ApiCard } from '../lib/queries';

export function BigCard({
  card,
  revealed,
  onReveal,
  pageTitle,
}: {
  card: ApiCard;
  revealed: boolean;
  onReveal: () => void;
  pageTitle?: string;
}) {
  const { t } = useTranslation('review');

  return (
    <div
      style={{
        maxWidth: 780,
        width: '100%',
        background: colors.surface,
        border: `1px solid ${colors.line2}`,
        borderRadius: 14,
        overflow: 'hidden',
        boxShadow:
          '0 1px 0 rgba(255,255,255,0.5) inset, 0 30px 60px -30px rgba(24,22,15,0.22), 0 10px 20px -12px rgba(24,22,15,0.08)',
        position: 'relative',
      }}
    >
      <span
        style={{
          position: 'absolute',
          top: 0,
          left: 40,
          right: 40,
          height: 3,
          background: colors.accent,
          borderRadius: '0 0 2px 2px',
        }}
      />

      <div
        style={{
          padding: '14px 32px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: `1px solid ${colors.line}`,
        }}
      >
        <span
          style={{
            fontVariantNumeric: 'tabular-nums',
            fontSize: 10,
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
            color: colors.accent,
            background: colors.accentSoft,
            padding: '3px 8px',
            borderRadius: 3,
          }}
        >
          {card.type}
        </span>
        {pageTitle && (
          <span
            style={{
              fontSize: 11,
              color: colors.ink3,
              letterSpacing: '0.04em',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              maxWidth: 340,
            }}
          >
            {pageTitle}
          </span>
        )}
      </div>

      <div style={{ padding: '44px 40px 36px' }}>
        {card.type === 'cloze' ? (
          <ClozeText
            text={card.front}
            revealed={revealed}
            targetGroup={
              typeof (card.extra as { clozeGroup?: unknown } | null)?.clozeGroup === 'number'
                ? ((card.extra as { clozeGroup: number }).clozeGroup)
                : null
            }
          />
        ) : (
          <Prompt text={card.front} />
        )}
      </div>

      {revealed ? (
        <div
          style={{
            padding: '24px 40px 32px',
            borderTop: `1px dashed ${colors.line2}`,
            background: colors.surface2,
          }}
        >
          {card.type === 'cloze' ? (
            card.back.trim() ? (
              <Answer label={t('memory_aid')} text={card.back} />
            ) : null
          ) : (
            <Answer label={t('readings')} text={card.back} />
          )}
        </div>
      ) : (
        <div
          style={{
            padding: '20px 40px 30px',
            textAlign: 'center',
            borderTop: `1px dashed ${colors.line2}`,
          }}
        >
          <button onClick={onReveal} style={revealBtn}>
            {t('reveal')}
            <kbd style={kbdInline}>{t('kbd_reveal')}</kbd>
          </button>
        </div>
      )}
    </div>
  );
}

function Prompt({ text }: { text: string }) {
  return (
    <div
      style={{
        fontWeight: 600,
        letterSpacing: '-0.018em',
        fontSize: 30,
        lineHeight: 1.25,
        color: colors.ink,
        whiteSpace: 'pre-wrap',
      }}
    >
      {renderWithMedia(text)}
    </div>
  );
}

// Inline media syntax: `![media:<id>:<kind>]`. Kind is image | audio | pdf.
// We render the media inline with the surrounding prose.
const MEDIA_RE = /!\[media:([0-9a-f-]+):(image|audio|pdf)(?::([^\]]*))?\]/g;

export function renderWithMedia(text: string): React.ReactNode[] {
  const out: React.ReactNode[] = [];
  let last = 0;
  let match: RegExpExecArray | null;
  MEDIA_RE.lastIndex = 0;
  while ((match = MEDIA_RE.exec(text)) !== null) {
    if (match.index > last) out.push(text.slice(last, match.index));
    const [, id, kind, label = ''] = match;
    out.push(<InlineMedia key={match.index} id={id!} kind={kind as 'image' | 'audio' | 'pdf'} label={label} />);
    last = match.index + match[0].length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

function InlineMedia({
  id,
  kind,
  label,
}: {
  id: string;
  kind: 'image' | 'audio' | 'pdf';
  label: string;
}) {
  const src = useMediaBlobUrl(id);
  if (!src) {
    return (
      <span
        style={{
          display: 'inline-block',
          padding: '4px 10px',
          fontSize: 11,
          color: colors.ink3,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
        }}
      >
        Loading…
      </span>
    );
  }
  if (kind === 'image') {
    return (
      <img
        src={src}
        alt={label}
        style={{
          display: 'block',
          maxWidth: '100%',
          borderRadius: 8,
          margin: '10px auto',
        }}
      />
    );
  }
  if (kind === 'audio') {
    return <audio src={src} controls style={{ display: 'block', margin: '10px 0' }} />;
  }
  return (
    <iframe
      src={`${src}#toolbar=0&navpanes=0&scrollbar=0`}
      title={label || 'PDF'}
      style={{
        display: 'block',
        width: '100%',
        height: 360,
        border: `1px solid ${colors.line2}`,
        borderRadius: 8,
        margin: '10px 0',
      }}
    />
  );
}

// Renders cloze text with each {{cN::...}} marker as either a blank (before
// reveal), highlighted inline (after reveal), or — for non-target groups —
// shown as plain revealed text the whole time. When targetGroup is null every
// cloze is blanked (single-group legacy cards).
function ClozeText({
  text,
  revealed,
  targetGroup,
}: {
  text: string;
  revealed: boolean;
  targetGroup: number | null;
}) {
  const parts = useMemo(() => splitCloze(text), [text]);
  return (
    <div
      style={{
        fontWeight: 500,
        letterSpacing: '-0.01em',
        fontSize: 24,
        lineHeight: 1.45,
        color: colors.ink,
        whiteSpace: 'pre-wrap',
      }}
    >
      {parts.map((p, i) => {
        if (p.kind === 'text') return <Fragment key={i}>{p.text}</Fragment>;
        const isTarget = targetGroup == null || p.group === targetGroup;
        if (!isTarget) {
          // Sibling group: always show the answer inline, but dimmed so the
          // learner's eye lands on the real blank first.
          return (
            <span key={i} style={clozeSibling}>
              {p.text}
            </span>
          );
        }
        return (
          <span key={i} style={revealed ? clozeRevealed : clozeBlank}>
            {revealed ? p.text : '\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0'}
          </span>
        );
      })}
    </div>
  );
}

type ClozePart =
  | { kind: 'text'; text: string }
  | { kind: 'cloze'; text: string; group: number };

function splitCloze(raw: string): ClozePart[] {
  const re = /\{\{c(\d+)::([^}]+)\}\}/g;
  const out: ClozePart[] = [];
  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(raw)) !== null) {
    if (m.index > last) out.push({ kind: 'text', text: raw.slice(last, m.index) });
    out.push({ kind: 'cloze', text: m[2]!, group: Number(m[1]) });
    last = m.index + m[0].length;
  }
  if (last < raw.length) out.push({ kind: 'text', text: raw.slice(last) });
  if (out.length === 0) out.push({ kind: 'text', text: raw });
  return out;
}

function Answer({ label, text }: { label: string; text: string }) {
  return (
    <div>
      <div
        style={{
          fontVariantNumeric: 'tabular-nums',
          fontSize: 9.5,
          letterSpacing: '0.12em',
          textTransform: 'uppercase',
          color: colors.ink3,
          fontWeight: 500,
          marginBottom: 10,
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontWeight: 500,
          letterSpacing: '-0.01em',
          fontSize: 22,
          lineHeight: 1.4,
          color: colors.ink,
          whiteSpace: 'pre-wrap',
        }}
      >
        {renderWithMedia(text)}
      </div>
    </div>
  );
}

const clozeBlank: React.CSSProperties = {
  display: 'inline-block',
  minWidth: 80,
  borderBottom: `2px solid ${colors.accent}`,
  margin: '0 2px',
};

const clozeRevealed: React.CSSProperties = {
  background: colors.accentSoft,
  color: colors.accent,
  padding: '0 6px',
  borderRadius: 3,
  fontWeight: 600,
};

// Non-target cloze (already answered by another card from the same note).
const clozeSibling: React.CSSProperties = {
  color: colors.ink3,
  background: 'rgba(24,22,15,0.04)',
  padding: '0 4px',
  borderRadius: 3,
};

const revealBtn: React.CSSProperties = {
  background: 'transparent',
  border: `1px solid ${colors.line2}`,
  color: colors.ink2,
  padding: '10px 18px',
  borderRadius: 8,
  fontFamily: 'inherit',
  fontSize: 13,
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 10,
};

const kbdInline: React.CSSProperties = {
  fontVariantNumeric: 'tabular-nums',
  fontSize: 10,
  background: 'rgba(24,22,15,0.06)',
  padding: '1px 6px',
  borderRadius: 3,
};
